import { createKbBackend } from './kb';
import { createSkillsBackend } from './skills';
import { createVfsBackend } from './vfs';
import type { FilesBackend } from './types';

/**
 * Pick the right backend for an owner ID:
 *   - `kbs-…`  →  KB API                  (`/api/v1/kb/{kbId}`)
 *   - `skl-…`  →  dedicated skill files   (`/api/v1/skills/{skillId}/files`)
 *   - `con-…`  →  unified VFS             (`/api/v1/vfs/{ownerId}`)
 *
 * Unknown prefixes fall back to the legacy VFS backend (which will 404
 * deterministically and surface a clear error in the UI rather than
 * silently rendering nothing).
 */
export function resolveFilesBackend(ownerId: string): FilesBackend {
  if (isKbOwner(ownerId)) return createKbBackend(ownerId);
  if (isSkillOwner(ownerId)) return createSkillsBackend(ownerId);
  return createVfsBackend(ownerId);
}

/** True when `ownerId` addresses a skill (`skl-…`) — routed to the
 *  dedicated skill-files API rather than the unified VFS. */
export function isSkillOwner(ownerId: string): boolean {
  return ownerId.startsWith('skl-');
}

/**
 * True when `ownerId` addresses a knowledge base (`kbs-…`) rather than a
 * conversation/skill VFS owner. Single source of truth for the prefix check
 * so direction detection (e.g. cross-store drag-and-drop in `AuiFiles`)
 * stays in sync with `resolveFilesBackend`.
 */
export function isKbOwner(ownerId: string): boolean {
  return ownerId.startsWith('kbs-');
}
